<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('adminLeave.title')" :description="t('adminLeave.description')">
      <template #actions>
        <AppButton variant="outline" :disabled="loading.data" @click="refreshData">
          <RefreshCw :class="['h-4 w-4 mr-2', loading.data ? 'animate-spin' : '']" />{{ t('common.refresh') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Stat chips -->
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
      <button @click="setStatus('pending')"
        :class="['flex flex-col gap-1 p-4 rounded-xl border transition-all text-left', statusFilter === 'pending' ? 'border-primary bg-primary/5 ring-1 ring-primary/20' : 'border-border bg-card hover:bg-muted/30']">
        <span class="text-xs font-medium text-muted-foreground">{{ t('common.pending') }}</span>
        <span class="text-2xl font-bold text-foreground">{{ pendingRequests.length }}</span>
        <span class="text-xs text-muted-foreground">{{ t('adminLeave.needsReview') }}</span>
      </button>
      <button @click="setStatus('approved')"
        :class="['flex flex-col gap-1 p-4 rounded-xl border transition-all text-left', statusFilter === 'approved' ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-1 ring-green-500/20' : 'border-border bg-card hover:bg-muted/30']">
        <span class="text-xs font-medium text-muted-foreground">{{ t('adminDashboard.onLeave') }}</span>
        <span class="text-2xl font-bold text-foreground">{{ onLeaveTodayCount }}</span>
        <span class="text-xs text-muted-foreground">{{ t('adminLeave.employeesOut') }}</span>
      </button>
      <button @click="setStatus('approved')"
        :class="['flex flex-col gap-1 p-4 rounded-xl border transition-all text-left', 'border-border bg-card hover:bg-muted/30']">
        <span class="text-xs font-medium text-muted-foreground">{{ t('adminLeave.approvedToday') }}</span>
        <span class="text-2xl font-bold text-foreground">{{ approvedTodayCount }}</span>
        <span class="text-xs text-muted-foreground">{{ t('adminLeave.thisSession') }}</span>
      </button>
      <button @click="filterConflicts"
        :class="['flex flex-col gap-1 p-4 rounded-xl border transition-all text-left', showConflictsOnly ? 'border-destructive bg-destructive/5 ring-1 ring-destructive/20' : 'border-border bg-card hover:bg-muted/30']">
        <span class="text-xs font-medium text-muted-foreground">{{ t('adminLeave.withConflicts') }}</span>
        <span class="text-2xl font-bold" :class="conflictCount > 0 ? 'text-destructive' : 'text-foreground'">{{ conflictCount }}</span>
        <span class="text-xs text-muted-foreground">{{ t('adminLeave.schedulingConflicts') }}</span>
      </button>
    </div>

    <!-- Table card -->
    <AppCard>

      <!-- Filter bar -->
      <div class="px-4 py-3 border-b border-border flex flex-wrap items-end gap-3">
        <div class="flex-1 min-w-40 flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('common.search') }}</label>
          <div class="relative">
            <Search class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input v-model="searchQuery" type="text" :placeholder="t('adminLeave.searchPlaceholder')"
              class="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('common.department') }}</label>
          <select v-model="deptFilter" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('adminAttendance.allDepartments') }}</option>
            <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('adminLeave.leaveType') }}</label>
          <select v-model="typeFilter" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('adminLeave.allTypes') }}</option>
            <option v-for="lt in uniqueLeaveTypes" :key="lt" :value="lt">{{ lt }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('common.status') }}</label>
          <select v-model="statusFilter" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('adminAttendance.allStatuses') }}</option>
            <option value="pending">{{ t('common.pending') }}</option>
            <option value="approved">{{ t('common.approved') }}</option>
            <option value="rejected">{{ t('common.rejected') }}</option>
          </select>
        </div>
        <AppButton variant="outline" size="sm" @click="clearFilters">{{ t('common.clear') }}</AppButton>
      </div>

      <!-- Bulk action bar -->
      <div v-if="selectedIds.size > 0"
        class="px-4 py-2 border-b border-border bg-primary/5 flex items-center justify-between gap-3">
        <span class="text-sm font-medium text-foreground">{{ selectedIds.size }} {{ t('adminLeave.selected') }}</span>
        <div class="flex items-center gap-2">
          <AppButton size="sm" @click="bulkApprove">
            <CheckCircle class="h-3.5 w-3.5 mr-1.5" />{{ t('adminLeave.approveAll') }}
          </AppButton>
          <AppButton size="sm" variant="destructive" @click="openBulkReject">
            <XCircle class="h-3.5 w-3.5 mr-1.5" />Reject All
          </AppButton>
          <AppButton size="sm" variant="ghost" @click="selectedIds.clear()">Deselect</AppButton>
        </div>
      </div>

      <!-- Table -->
      <div v-if="loading.data" class="flex items-center justify-center py-12"><AppSpinner size="lg" /></div>
      <AppEmptyState v-else-if="filtered.length === 0" :icon="FileText" title="No leave requests found" description="Try adjusting your filters." />

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-border bg-muted/20">
              <th class="w-10 px-4 py-3">
                <input type="checkbox" class="rounded" :checked="allSelected" @change="toggleAll" />
              </th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Employee</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Leave Type</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Dates</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">Days</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Submitted</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Status</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            <tr v-for="req in paginated" :key="req.id"
              :class="['transition-colors', selectedIds.has(req.id) ? 'bg-primary/5' : 'hover:bg-muted/20']">
              <!-- Checkbox -->
              <td class="px-4 py-3">
                <input type="checkbox" class="rounded"
                  :checked="selectedIds.has(req.id)"
                  @change="toggleRow(req.id)" />
              </td>
              <!-- Employee -->
              <td class="px-4 py-3">
                <div class="flex items-center gap-2.5">
                  <div class="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 text-xs font-bold text-primary">
                    {{ initials(req.employeeName || req.employee_name) }}
                  </div>
                  <div>
                    <p class="font-medium text-foreground leading-none">{{ req.employeeName || req.employee_name }}</p>
                    <p class="text-xs text-muted-foreground mt-0.5">{{ req.department }} · {{ req.employeeId || req.employee_id }}</p>
                  </div>
                </div>
                <div v-if="req.conflicts && req.conflicts.length" class="mt-1 flex items-center gap-1 text-xs text-destructive font-medium">
                  <AlertTriangle class="h-3 w-3" />{{ req.conflicts.length }} conflict{{ req.conflicts.length > 1 ? 's' : '' }}
                </div>
              </td>
              <!-- Type -->
              <td class="px-4 py-3">
                <span class="text-foreground">{{ req.leaveType || req.leave_type }}</span>
              </td>
              <!-- Dates -->
              <td class="px-4 py-3 whitespace-nowrap">
                <template v-if="isTimedShortLeave(req)">
                  <p class="text-foreground">{{ fmtDate(req.startDate || req.start_date) }}</p>
                  <p class="text-xs text-muted-foreground">{{ fmtTime(req.startDate || req.start_date) }} – {{ fmtTime(req.endDate || req.end_date) }}</p>
                </template>
                <template v-else>
                  <p class="text-foreground">{{ fmtDate(req.startDate || req.start_date) }}</p>
                  <p v-if="(req.startDate || req.start_date) !== (req.endDate || req.end_date)" class="text-xs text-muted-foreground">→ {{ fmtDate(req.endDate || req.end_date) }}</p>
                </template>
              </td>
              <!-- Days -->
              <td class="px-4 py-3 hidden md:table-cell">
                <span class="font-medium text-foreground">{{ fmtDuration(req) }}</span>
              </td>
              <!-- Submitted -->
              <td class="px-4 py-3 hidden lg:table-cell text-muted-foreground">
                {{ fmtRelative(req.submittedAt || req.submitted_at || req.created_at) }}
              </td>
              <!-- Status -->
              <td class="px-4 py-3">
                <AppBadge :variant="statusVariant(req.status)">{{ req.status }}</AppBadge>
              </td>
              <!-- Actions -->
              <td class="px-4 py-3">
                <div class="flex items-center gap-1">
                  <AppButton size="icon-sm" variant="ghost" title="View details" @click="openDetails(req)">
                    <Eye class="h-3.5 w-3.5" />
                  </AppButton>
                  <template v-if="req.status === 'pending'">
                    <AppButton size="icon-sm" variant="ghost" class="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20" title="Approve" @click="openApprove(req)">
                      <CheckCircle class="h-3.5 w-3.5" />
                    </AppButton>
                    <AppButton size="icon-sm" variant="ghost" class="text-destructive hover:bg-destructive/10" title="Reject" @click="openReject(req)">
                      <XCircle class="h-3.5 w-3.5" />
                    </AppButton>
                  </template>
                  <template v-if="req.status === 'approved'">
                    <AppButton size="icon-sm" variant="ghost" class="text-muted-foreground" title="Cancel leave" @click="openCancel(req)">
                      <Ban class="h-3.5 w-3.5" />
                    </AppButton>
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="pageCount > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <span class="text-sm text-muted-foreground">
          {{ (page - 1) * perPage + 1 }}–{{ Math.min(page * perPage, filtered.length) }} of {{ filtered.length }}
        </span>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="page === 1" @click="page--">
            <ChevronLeft class="h-3.5 w-3.5" />
          </AppButton>
          <button v-for="p in visiblePages" :key="p" @click="page = p"
            :class="['h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors',
              p === page ? 'bg-primary text-primary-foreground' : 'border border-border text-foreground hover:bg-accent']">
            {{ p }}
          </button>
          <AppButton size="icon-sm" variant="outline" :disabled="page === pageCount" @click="page++">
            <ChevronRight class="h-3.5 w-3.5" />
          </AppButton>
        </div>
      </div>

    </AppCard>

    <!-- ── Modals ─────────────────────────────────────────── -->

    <!-- Details Modal -->
    <AppModal v-model="showDetails" title="Leave Request Details" size="lg">
      <div v-if="activeReq" class="flex flex-col gap-5">
        <!-- Employee row -->
        <div class="flex items-center gap-3 pb-4 border-b border-border">
          <div class="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center text-base font-bold text-primary shrink-0">
            {{ initials(activeReq.employeeName || activeReq.employee_name) }}
          </div>
          <div>
            <p class="font-semibold text-foreground">{{ activeReq.employeeName || activeReq.employee_name }}</p>
            <p class="text-sm text-muted-foreground">{{ activeReq.department }} · {{ activeReq.employeeId || activeReq.employee_id }}</p>
          </div>
          <AppBadge :variant="statusVariant(activeReq.status)" class="ml-auto">{{ activeReq.status }}</AppBadge>
        </div>

        <!-- Key info grid -->
        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div><p class="text-xs text-muted-foreground mb-0.5">Leave Type</p><p class="font-medium text-foreground">{{ activeReq.leaveType || activeReq.leave_type }}</p></div>
          <div><p class="text-xs text-muted-foreground mb-0.5">{{ isTimedShortLeave(activeReq) ? 'From' : 'Start Date' }}</p><p class="font-medium text-foreground">{{ fmtDate(activeReq.startDate || activeReq.start_date) }}<span v-if="isTimedShortLeave(activeReq)"> · {{ fmtTime(activeReq.startDate || activeReq.start_date) }}</span></p></div>
          <div><p class="text-xs text-muted-foreground mb-0.5">{{ isTimedShortLeave(activeReq) ? 'To' : 'End Date' }}</p><p class="font-medium text-foreground">{{ isTimedShortLeave(activeReq) ? fmtTime(activeReq.endDate || activeReq.end_date) : fmtDate(activeReq.endDate || activeReq.end_date) }}</p></div>
          <div><p class="text-xs text-muted-foreground mb-0.5">Duration</p><p class="font-medium text-foreground">{{ fmtDuration(activeReq) }}</p></div>
          <div><p class="text-xs text-muted-foreground mb-0.5">Balance Remaining</p><p class="font-medium text-foreground">{{ activeReq.currentBalance ?? activeReq.current_balance ?? '—' }} days</p></div>
          <div><p class="text-xs text-muted-foreground mb-0.5">Coverage Arranged</p>
            <span :class="['text-xs font-semibold', activeReq.coverageArranged ? 'text-green-600' : 'text-destructive']">{{ activeReq.coverageArranged ? 'Yes' : 'No' }}</span>
          </div>
          <div class="md:col-span-3"><p class="text-xs text-muted-foreground mb-0.5">Reason</p><p class="text-foreground">{{ activeReq.reason || '—' }}</p></div>
        </div>

        <!-- Conflicts -->
        <div v-if="activeReq.conflicts && activeReq.conflicts.length" class="p-3 rounded-lg border border-destructive/30 bg-destructive/5">
          <p class="text-xs font-semibold text-destructive mb-2 flex items-center gap-1.5"><AlertTriangle class="h-3.5 w-3.5" />Scheduling Conflicts</p>
          <div v-for="c in activeReq.conflicts" :key="c.id" class="text-xs text-muted-foreground">
            {{ c.employeeName }} ({{ c.department }}) — {{ c.dates }}
          </div>
        </div>

        <!-- Documents button -->
        <button @click="showDocumentsModal = true" class="flex items-center gap-2 text-sm text-primary hover:underline">
          <FileText class="h-4 w-4" />View attached documents
        </button>
      </div>
      <template #footer>
        <div class="flex justify-between gap-2">
          <AppButton variant="outline" @click="showDetails = false">Close</AppButton>
          <div v-if="activeReq?.status === 'pending'" class="flex gap-2">
            <AppButton variant="destructive" @click="showDetails = false; openReject(activeReq)">Reject</AppButton>
            <AppButton @click="showDetails = false; openApprove(activeReq)">Approve</AppButton>
          </div>
        </div>
      </template>
    </AppModal>

    <!-- Approve Modal -->
    <AppModal v-model="showApproveModal" title="Approve Leave Request" size="sm">
      <div v-if="activeReq" class="flex flex-col gap-4">
        <p class="text-sm text-muted-foreground">
          Approve <strong class="text-foreground">{{ fmtDuration(activeReq) }}</strong> of
          <strong class="text-foreground">{{ activeReq.leaveType || activeReq.leave_type }}</strong> for
          <strong class="text-foreground">{{ activeReq.employeeName || activeReq.employee_name }}</strong>?
        </p>
        <div class="text-xs text-muted-foreground bg-muted/40 rounded-lg px-3 py-2">
          {{ fmtDate(activeReq.startDate || activeReq.start_date) }} → {{ fmtDate(activeReq.endDate || activeReq.end_date) }}
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Note <span class="text-muted-foreground font-normal">(optional)</span></label>
          <textarea v-model="approveNote" rows="2" placeholder="Add a note for the employee…"
            class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" :disabled="loading.action" @click="showApproveModal = false">Cancel</AppButton>
          <AppButton :disabled="loading.action" @click="confirmApprove">
            {{ loading.action ? 'Approving…' : 'Approve' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Reject Modal -->
    <AppModal v-model="showRejectModal" :title="isBulkReject ? `Reject ${selectedIds.size} Requests` : 'Reject Leave Request'" size="sm">
      <div class="flex flex-col gap-4">
        <p v-if="!isBulkReject && activeReq" class="text-sm text-muted-foreground">
          Reject <strong class="text-foreground">{{ activeReq.leaveType || activeReq.leave_type }}</strong> for
          <strong class="text-foreground">{{ activeReq.employeeName || activeReq.employee_name }}</strong>?
        </p>
        <p v-else class="text-sm text-muted-foreground">Reject all {{ selectedIds.size }} selected leave requests?</p>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason <span class="text-destructive">*</span></label>
          <textarea v-model="rejectReason" rows="3" placeholder="Explain why this request is being rejected…"
            class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            :class="rejectReasonError ? 'border-destructive' : ''"></textarea>
          <p v-if="rejectReasonError" class="text-xs text-destructive">A reason is required.</p>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" :disabled="loading.action" @click="showRejectModal = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="loading.action" @click="confirmReject">
            {{ loading.action ? 'Rejecting…' : 'Reject' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Cancel Modal -->
    <AppModal v-model="showCancelModal" title="Cancel Approved Leave" size="sm">
      <div v-if="activeReq" class="flex flex-col gap-4">
        <p class="text-sm text-muted-foreground">
          Cancel approved leave for <strong class="text-foreground">{{ activeReq.employeeName || activeReq.employee_name }}</strong>?
          The balance days will be restored.
        </p>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason <span class="text-destructive">*</span></label>
          <textarea v-model="cancelReason" rows="2" placeholder="Reason for cancellation…"
            class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" :disabled="loading.action" @click="showCancelModal = false">Cancel</AppButton>
          <AppButton variant="destructive" :disabled="loading.action" @click="confirmCancel">
            {{ loading.action ? 'Cancelling…' : 'Cancel Leave' }}
          </AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Toast -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 translate-y-2"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0 translate-y-2">
        <div v-if="toast.show"
          class="fixed bottom-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl border max-w-sm text-sm font-medium"
          :class="{
            'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/30 dark:border-green-800 dark:text-green-300': toast.type === 'success',
            'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300': toast.type === 'error',
            'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900/30 dark:border-yellow-800 dark:text-yellow-300': toast.type === 'info',
          }">
          <CheckCircle v-if="toast.type === 'success'" class="h-4 w-4 shrink-0" />
          <XCircle v-else-if="toast.type === 'error'" class="h-4 w-4 shrink-0" />
          <AlertTriangle v-else class="h-4 w-4 shrink-0" />
          {{ toast.message }}
          <button @click="toast.show = false" class="ml-auto shrink-0"><X class="h-4 w-4" /></button>
        </div>
      </Transition>
    </Teleport>

    <!-- Leave Documents Modal -->
    <LeaveDocumentsModal
      v-if="showDocumentsModal && activeReq"
      :leaveRequest="activeReq"
      @close="showDocumentsModal = false"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  RefreshCw, Search, FileText, CheckCircle, XCircle, AlertTriangle,
  X, Eye, Ban, ChevronLeft, ChevronRight
} from 'lucide-vue-next';

import LeaveDocumentsModal from '@/components/leave/LeaveDocumentsModal.vue';
import { AppPageHeader, AppCard, AppBadge, AppButton, AppModal, AppEmptyState, AppSpinner } from '@/components/ui';
import adminService from '@/services/adminService';

const { t } = useI18n();

// ── State ─────────────────────────────────────────────────────
const pendingRequests = ref([]);
const archiveRequests = ref([]);
const departments = ref([]);

const loading = ref({ data: false, action: false });
const toast = ref({ show: false, type: 'success', message: '' });

// Filters
const searchQuery = ref('');
const deptFilter = ref('');
const typeFilter = ref('');
const statusFilter = ref('pending');
const showConflictsOnly = ref(false);

// Pagination
const page = ref(1);
const perPage = 15;

// Selection
const selectedIds = ref(new Set());

// Modals
const activeReq = ref(null);
const showDetails = ref(false);
const showApproveModal = ref(false);
const showRejectModal = ref(false);
const showCancelModal = ref(false);
const showDocumentsModal = ref(false);
const isBulkReject = ref(false);

// Form state
const approveNote = ref('');
const rejectReason = ref('');
const rejectReasonError = ref(false);
const cancelReason = ref('');

// ── Computed ──────────────────────────────────────────────────
const allRequests = computed(() => [...pendingRequests.value, ...archiveRequests.value]);

const uniqueLeaveTypes = computed(() => {
  const types = new Set(allRequests.value.map(r => r.leaveType || r.leave_type).filter(Boolean));
  return [...types].sort();
});

const onLeaveTodayCount = computed(() => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return allRequests.value.filter(r => {
    if (r.status !== 'approved') return false;
    const s = new Date(r.startDate || r.start_date);
    const e = new Date(r.endDate || r.end_date);
    return today >= s && today <= e;
  }).length;
});

const approvedTodayCount = computed(() => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return allRequests.value.filter(r => {
    if (r.status !== 'approved') return false;
    const d = new Date(r.updatedAt || r.updated_at || r.approvedAt || r.approved_at || 0);
    d.setHours(0, 0, 0, 0);
    return d.getTime() === today.getTime();
  }).length;
});

const conflictCount = computed(() =>
  pendingRequests.value.filter(r => r.conflicts && r.conflicts.length > 0).length
);

const filtered = computed(() => {
  let src = statusFilter.value === 'pending' ? pendingRequests.value
    : statusFilter.value ? archiveRequests.value.filter(r => r.status === statusFilter.value)
    : allRequests.value;

  if (showConflictsOnly.value) src = src.filter(r => r.conflicts && r.conflicts.length > 0);

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    src = src.filter(r =>
      (r.employeeName || r.employee_name || '').toLowerCase().includes(q) ||
      (r.department || '').toLowerCase().includes(q) ||
      (r.employeeId || r.employee_id || '').toString().toLowerCase().includes(q)
    );
  }
  if (deptFilter.value)
    src = src.filter(r => r.departmentId === deptFilter.value || r.department_id === deptFilter.value);
  if (typeFilter.value)
    src = src.filter(r => (r.leaveType || r.leave_type) === typeFilter.value);

  return src;
});

const pageCount = computed(() => Math.max(1, Math.ceil(filtered.value.length / perPage)));
const paginated = computed(() => filtered.value.slice((page.value - 1) * perPage, page.value * perPage));

const visiblePages = computed(() => {
  const total = pageCount.value;
  const cur = page.value;
  const delta = 2;
  const range = [];
  for (let i = Math.max(2, cur - delta); i <= Math.min(total - 1, cur + delta); i++) range.push(i);
  if (cur - delta > 2) range.unshift('…');
  if (cur + delta < total - 1) range.push('…');
  range.unshift(1);
  if (total > 1) range.push(total);
  return range.filter((v, i, a) => a.indexOf(v) === i);
});

const allSelected = computed(() =>
  paginated.value.length > 0 && paginated.value.every(r => selectedIds.value.has(r.id))
);

// ── Helpers ───────────────────────────────────────────────────
const initials = (name = '') => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// Short leave requests submitted via the manual form carry an actual clock
// time in start_date/end_date; ones auto-created by the clock in/out system
// store midnight placeholders there (real times only live in duration_*).
const isTimedShortLeave = (req) => {
  if (!req || !(req.is_short_leave || req.isShortLeave)) return false;
  const start = req.startDate || req.start_date;
  if (!start) return false;
  const d = new Date(start);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
};

const fmtDuration = (req) => {
  if (!req) return '—';
  if (req.is_short_leave || req.isShortLeave) {
    const hours = req.duration_hours ?? req.durationHours;
    const minutes = req.duration_minutes ?? req.durationMinutes;
    if (minutes != null) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      if (h > 0 && m > 0) return `${h}h ${m}m`;
      if (h > 0) return `${h} hour${h !== 1 ? 's' : ''}`;
      return `${m} min`;
    }
    if (hours != null) return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  const days = req.days;
  return days != null ? `${days} day${days !== 1 ? 's' : ''}` : '—';
};

const fmtRelative = (d) => {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(ms / 3600000);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(ms / 86400000);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return fmtDate(d);
};

const statusVariant = (s) => ({ pending: 'warning', approved: 'success', rejected: 'destructive', cancelled: 'muted' }[s] || 'muted');

const showToast = (type, message) => {
  toast.value = { show: true, type, message };
  setTimeout(() => { toast.value.show = false; }, 4000);
};

// ── Filter helpers ────────────────────────────────────────────
const setStatus = (s) => { statusFilter.value = s; showConflictsOnly.value = false; page.value = 1; };
const filterConflicts = () => { showConflictsOnly.value = !showConflictsOnly.value; statusFilter.value = 'pending'; page.value = 1; };
const clearFilters = () => { searchQuery.value = ''; deptFilter.value = ''; typeFilter.value = ''; statusFilter.value = 'pending'; showConflictsOnly.value = false; page.value = 1; };

// ── Selection ─────────────────────────────────────────────────
const toggleRow = (id) => {
  const s = new Set(selectedIds.value);
  s.has(id) ? s.delete(id) : s.add(id);
  selectedIds.value = s;
};
const toggleAll = () => {
  if (allSelected.value) {
    const s = new Set(selectedIds.value);
    paginated.value.forEach(r => s.delete(r.id));
    selectedIds.value = s;
  } else {
    const s = new Set(selectedIds.value);
    paginated.value.forEach(r => s.add(r.id));
    selectedIds.value = s;
  }
};

// ── Modal openers ─────────────────────────────────────────────
const openDetails = (req) => { activeReq.value = req; showDetails.value = true; };
const openApprove = (req) => { activeReq.value = req; approveNote.value = ''; showApproveModal.value = true; };
const openReject = (req) => { activeReq.value = req; rejectReason.value = ''; rejectReasonError.value = false; isBulkReject.value = false; showRejectModal.value = true; };
const openBulkReject = () => { activeReq.value = null; rejectReason.value = ''; rejectReasonError.value = false; isBulkReject.value = true; showRejectModal.value = true; };
const openCancel = (req) => { activeReq.value = req; cancelReason.value = ''; showCancelModal.value = true; };

// ── Actions ───────────────────────────────────────────────────
const confirmApprove = async () => {
  loading.value.action = true;
  try {
    await adminService.approveLeaveRequest(activeReq.value.id, approveNote.value);
    showToast('success', `Approved leave for ${activeReq.value.employeeName || activeReq.value.employee_name}`);
    showApproveModal.value = false;
    await loadAll();
  } catch (e) {
    showToast('error', e.response?.data?.message || 'Failed to approve');
  } finally { loading.value.action = false; }
};

const confirmReject = async () => {
  if (!rejectReason.value.trim()) { rejectReasonError.value = true; return; }
  rejectReasonError.value = false;
  loading.value.action = true;
  try {
    if (isBulkReject.value) {
      const ids = [...selectedIds.value];
      await Promise.all(ids.map(id => adminService.rejectLeaveRequest(id, rejectReason.value)));
      showToast('success', `Rejected ${ids.length} requests`);
      selectedIds.value = new Set();
    } else {
      await adminService.rejectLeaveRequest(activeReq.value.id, rejectReason.value);
      showToast('success', `Rejected leave for ${activeReq.value.employeeName || activeReq.value.employee_name}`);
    }
    showRejectModal.value = false;
    await loadAll();
  } catch (e) {
    showToast('error', e.response?.data?.message || 'Failed to reject');
  } finally { loading.value.action = false; }
};

const confirmCancel = async () => {
  if (!cancelReason.value.trim()) return;
  loading.value.action = true;
  try {
    const result = await adminService.cancelLeaveRequest(activeReq.value.id, cancelReason.value);
    const days = result.data?.refundedDays || activeReq.value.days;
    showToast('success', `Leave cancelled. ${days} day(s) restored.`);
    showCancelModal.value = false;
    await loadAll();
  } catch (e) {
    showToast('error', e.response?.data?.message || 'Failed to cancel');
  } finally { loading.value.action = false; }
};

const bulkApprove = async () => {
  if (selectedIds.value.size === 0) return;
  loading.value.action = true;
  try {
    const ids = [...selectedIds.value];
    await Promise.all(ids.map(id => adminService.approveLeaveRequest(id)));
    showToast('success', `Approved ${ids.length} leave requests`);
    selectedIds.value = new Set();
    await loadAll();
  } catch (e) {
    showToast('error', 'Failed to approve some requests');
  } finally { loading.value.action = false; }
};

// ── Data loading ──────────────────────────────────────────────
const loadPending = async () => {
  const res = await adminService.getLeaveRequests({ status: 'pending', limit: 500 });
  pendingRequests.value = res.success && res.requests ? res.requests
    : res.success && Array.isArray(res.data) ? res.data
    : Array.isArray(res) ? res : [];
};

const loadArchive = async () => {
  const [approvedRes, rejectedRes] = await Promise.all([
    adminService.getLeaveRequests({ status: 'approved', limit: 500, sortBy: 'updated_at', sortOrder: 'DESC' }),
    adminService.getLeaveRequests({ status: 'rejected', limit: 500, sortBy: 'updated_at', sortOrder: 'DESC' }),
  ]);
  const approved = approvedRes.success && approvedRes.requests ? approvedRes.requests : [];
  const rejected = rejectedRes.success && rejectedRes.requests ? rejectedRes.requests : [];
  archiveRequests.value = [...approved, ...rejected].sort((a, b) =>
    new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
  );
};

const loadDepts = async () => {
  try {
    const depts = await adminService.getDepartments();
    departments.value = Array.isArray(depts)
      ? depts.map((d, i) => typeof d === 'string' ? { id: i + 1, name: d } : d)
      : [];
  } catch { departments.value = []; }
};

const loadAll = async () => {
  loading.value.data = true;
  try {
    await Promise.all([loadPending(), loadArchive(), loadDepts()]);
  } catch (e) {
    showToast('error', 'Failed to load data');
  } finally {
    loading.value.data = false;
  }
};

const refreshData = () => loadAll().then(() => showToast('success', 'Data refreshed'));

// Reset page on filter change
watch([searchQuery, deptFilter, typeFilter, statusFilter, showConflictsOnly], () => { page.value = 1; });

// Auto-refresh every 5 minutes
let timer;
onMounted(() => {
  loadAll();
  timer = setInterval(() => { if (!loading.value.data) loadAll(); }, 5 * 60 * 1000);
});
</script>
